import { RolePermissionAssignment } from "../../services/systemPermissions.service.ts";
import { permissionCopy, scopeIsAllowed } from "./permissionCopy.ts";

export type PermissionDraft = Record<string, { enabled: boolean; scope: string }>;

export function enabledAssignments(
  draft: PermissionDraft,
): RolePermissionAssignment[] {
  const seen = new Set<string>();
  const permissions: RolePermissionAssignment[] = [];
  for (const [code, value] of Object.entries(draft)) {
    if (!value?.enabled || !code) continue;
    if (seen.has(code)) continue;
    seen.add(code);
    permissions.push({
      code,
      scope: value.scope as RolePermissionAssignment["scope"],
    });
  }
  return permissions;
}

export function removedAssignments(
  before: PermissionDraft,
  after: PermissionDraft,
): string[] {
  return Object.entries(before)
    .filter(([code, value]) => value?.enabled && !after[code]?.enabled)
    .map(([code]) => code);
}

export function assignmentsMatch(
  sent: RolePermissionAssignment[],
  received: RolePermissionAssignment[] | undefined,
): boolean {
  const normalize = (rows: RolePermissionAssignment[]) =>
    [...rows]
      .map((row) => `${row.code}:${row.scope}`)
      .sort()
      .join("|");
  return normalize(sent) === normalize(received || []);
}

export function draftValidationError(
  draft: PermissionDraft,
  baseline: PermissionDraft,
): string {
  const enabled = Object.entries(draft).filter(([, value]) => value?.enabled);
  const codes = enabled.map(([code]) => code);
  if (codes.some((code) => !code)) return "Selecciona un permiso.";
  if (new Set(codes).size !== codes.length) return "El permiso ya está agregado.";
  for (const [code, value] of enabled) {
    if (!value.scope) return "Selecciona a qué registros tendrá acceso.";
    const preserved =
      baseline[code]?.enabled === true && baseline[code]?.scope === value.scope;
    if (!scopeIsAllowed(code, value.scope) && !preserved) {
      return "Este alcance no está disponible para este permiso.";
    }
  }
  return "";
}

export function rowScopeError(
  code: string,
  scope: string,
  enabled: boolean,
  baseline: PermissionDraft,
): string {
  if (!enabled) return "";
  if (!scope) return "Selecciona a qué registros tendrá acceso.";
  const preserved =
    baseline[code]?.enabled === true && baseline[code]?.scope === scope;
  if (!scopeIsAllowed(code, scope) && !preserved) {
    return "Este alcance no está disponible para este permiso.";
  }
  return "";
}

export function removalConfirmMessage(codes: string[]): string {
  if (!codes.length) return "";
  const names = codes.map((code) => permissionCopy(code).name).join(", ");
  const sensitive = codes.some((code) => permissionCopy(code).sensitive);
  if (sensitive) {
    return `Vas a quitar permisos delicados: ${names}. El guardado reemplaza toda la lista. ¿Continuar?`;
  }
  return `Vas a quitar: ${names}. El guardado reemplaza toda la lista. ¿Continuar?`;
}

export function permissionSaveMessage(error: any): string {
  const data = error?.response?.data ?? error;
  const status = error?.response?.status ?? error?.status ?? data?.status;
  const code = data?.code;
  if (code === "PERMISSION_REQUIRED") return "Selecciona un permiso.";
  if (code === "SCOPE_NOT_ALLOWED" || code === "SCOPE_INVALID") {
    return "Este alcance no está disponible para este permiso.";
  }
  if (code === "PERMISSION_DUPLICATE") return "El permiso ya está agregado.";
  if (code === "GRANT_CEILING") {
    return "No puedes asignar un alcance mayor al que tú tienes.";
  }
  if (code === "ROLE_STRUCTURAL") {
    return "No tienes autorización para administrar este rol.";
  }
  if (code === "SAVE_MISMATCH" || code === "SAVE_FAILED") {
    return "No se pudieron guardar los permisos.";
  }
  if (code === "PERMISSION_UNKNOWN") return "Hay permisos que no existen.";
  if (code === "ASSIGN_LOCK") {
    return "No se puede quitar el permiso para administrar roles: el sistema se quedaría sin alguien que pueda hacerlo.";
  }
  if (status === 401) return "La sesión expiró. Inicia sesión nuevamente.";
  if (status === 403) return "No tienes autorización para administrar este rol.";
  if (status === 404) return "No se encontró el rol.";
  if (status === 409) return "El permiso ya está agregado.";
  if (status === 400) {
    const raw = Array.isArray(data?.message)
      ? data.message.join(" ")
      : data?.message;
    const message =
      typeof raw === "string"
        ? raw.replace(/permissions\.\d+\./g, "").trim()
        : "";
    if (message.includes("alcance")) {
      return "Este alcance no está disponible para este permiso.";
    }
    if (message.includes("Selecciona un permiso")) {
      return "Selecciona un permiso.";
    }
    if (
      message.includes("permiso") &&
      !/select |insert |stack|queryfailed/i.test(message)
    ) {
      return message;
    }
    return "Revisa los permisos seleccionados.";
  }
  if (status >= 500 || status == null) return "No se pudieron guardar los permisos.";
  return "No se pudieron guardar los permisos.";
}
